// ═══════════════════════════════════════════════════════════════
// VISTA DE RECIBOS DE PAGO
// Soporta: Nómina Regular | Bonificación | Regalía Pascual
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Package, Loader2,
  Users, ChevronDown, AlertCircle, RefreshCw,
} from 'lucide-react';
import {
  getAvailablePayrolls,
  getPayrollEmployees,
  downloadPayslip,
  downloadBulkPayslips,
  getBonificacionesPagadas,
  getBonificacionDetalle,
  downloadBonificacionPayslip,
  downloadBonificacionBulk,
  getRegaliasPageadas,
  getRegaliaRecords,
  downloadRegaliaPayslip,
  downloadRegaliaBulk,
} from '../../../../api/payrollReports';

const fmt = (v) =>
  'RD$ ' + parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Configuración de cada tipo ───────────────────────────────
const TIPOS = [
  {
    id:          'nomina',
    label:       'Nómina Regular',
    montoLabel:  'Neto a Pagar',
    emptyMsg:    'Selecciona una nómina cerrada',
    emptyHint:   'Solo se muestran nóminas en estado cerrado',
    color:       'blue',
  },
  {
    id:          'bonificacion',
    label:       'Bonificación',
    montoLabel:  'Neto Bonificación',
    emptyMsg:    'Selecciona una bonificación pagada',
    emptyHint:   'Solo se muestran bonificaciones en estado pagado',
    color:       'purple',
  },
  {
    id:          'regalia',
    label:       'Regalía Pascual',
    montoLabel:  'Monto Regalía',
    emptyMsg:    'Selecciona un proceso de regalía pagado',
    emptyHint:   'Solo se muestran procesos en estado pagado',
    color:       'red',
  },
];

// ── Normalizar procesos para el selector ─────────────────────
function buildProcessLabel(tipo, p) {
  if (tipo === 'nomina') {
    return `${p.periodo}${p.quincena ? ` (${p.quincena})` : ''} — ${p.tipo} — Pago: ${p.fecha_pago}`;
  }
  if (tipo === 'bonificacion') {
    return `${p.nombre || `Bonificación ${p.anio}`} — Año ${p.anio}`;
  }
  return `Regalía Pascual ${p.anio}`;
}

// ── Normalizar registros de empleados ────────────────────────
function normalizeRecords(tipo, rows) {
  if (tipo === 'nomina') {
    return rows.map(r => ({
      id:           r.employee_id,
      downloadId:   r.employee_id,
      codigo:       r.codigo,
      nombre:       r.nombre,
      apellido:     r.apellido,
      departamento: r.departamento,
      monto:        r.neto_a_pagar,
    }));
  }
  if (tipo === 'bonificacion') {
    return rows.map(r => ({
      id:           r.id,
      downloadId:   r.id,
      codigo:       r.codigo || '—',
      nombre:       (r.apellidos_nombres || '').split(' ')[0] || '',
      apellido:     (r.apellidos_nombres || '').split(' ').slice(1).join(' ') || r.apellidos_nombres,
      departamento: r.departamento,
      monto:        r.neto_a_pagar,
    }));
  }
  // regalia
  return rows.map(r => ({
    id:           r.id,
    downloadId:   r.id,
    codigo:       r.codigo || '—',
    nombre:       r.nombre,
    apellido:     r.apellido,
    departamento: r.departamento,
    monto:        parseFloat(r.monto_ajustado ?? r.monto_calculado ?? 0),
  }));
}

// ── Loaders por tipo ─────────────────────────────────────────
async function loadProcesses(tipo, companyId) {
  if (tipo === 'nomina')       return getAvailablePayrolls(companyId).then(r => r.data || []);
  if (tipo === 'bonificacion') return getBonificacionesPagadas(companyId);
  return getRegaliasPageadas(companyId);
}

async function loadRecords(tipo, processId) {
  if (tipo === 'nomina')       return getPayrollEmployees(processId).then(r => r.data || []);
  if (tipo === 'bonificacion') return getBonificacionDetalle(processId);
  return getRegaliaRecords(processId);
}

async function downloadOne(tipo, processId, record, processLabel) {
  if (tipo === 'nomina') {
    await downloadPayslip(processId, record.downloadId, `recibo_${record.codigo}_${processLabel}.pdf`);
  } else if (tipo === 'bonificacion') {
    await downloadBonificacionPayslip(processId, record.downloadId, `bonificacion_${record.codigo}_${processLabel}.pdf`);
  } else {
    await downloadRegaliaPayslip(processId, record.downloadId, `regalia_${record.codigo}_${processLabel}.pdf`);
  }
}

async function downloadBulk(tipo, processId, processLabel) {
  if (tipo === 'nomina') {
    await downloadBulkPayslips(processId, `recibos_${processLabel}.zip`);
  } else if (tipo === 'bonificacion') {
    await downloadBonificacionBulk(processId, `bonificaciones_${processLabel}.zip`);
  } else {
    await downloadRegaliaBulk(processId, `regalia_${processLabel}.zip`);
  }
}

// ─────────────────────────────────────────────────────────────
export default function PayslipView({ companyId }) {
  const [tipo,        setTipo]        = useState('nomina');
  const [processes,   setProcesses]   = useState([]);
  const [processId,   setProcessId]   = useState('');
  const [records,     setRecords]     = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error,       setError]       = useState(null);

  const tipoConfig      = TIPOS.find(t => t.id === tipo);
  const selectedProcess = processes.find(p => p.id === processId);
  const processLabel    = selectedProcess ? buildProcessLabel(tipo, selectedProcess) : '';
  const colorCls        = { blue: 'blue', purple: 'purple', red: 'red' }[tipoConfig.color];

  // Recargar procesos cuando cambia el tipo
  useEffect(() => {
    if (!companyId) return;
    setProcessId('');
    setRecords([]);
    setError(null);
    setLoadingList(true);
    loadProcesses(tipo, companyId)
      .then(setProcesses)
      .catch(() => setError('Error al cargar la lista'))
      .finally(() => setLoadingList(false));
  }, [tipo, companyId]);

  // Recargar registros cuando cambia el proceso
  useEffect(() => {
    if (!processId) { setRecords([]); return; }
    setLoadingRecs(true);
    setError(null);
    loadRecords(tipo, processId)
      .then(rows => setRecords(normalizeRecords(tipo, rows)))
      .catch(() => setError('Error al cargar empleados'))
      .finally(() => setLoadingRecs(false));
  }, [tipo, processId]);

  const handleDownloadOne = async (record) => {
    try {
      setDownloading(record.id);
      await downloadOne(tipo, processId, record, processLabel);
    } catch {
      setError('Error al generar el comprobante');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadBulk = async () => {
    try {
      setDownloading('bulk');
      await downloadBulk(tipo, processId, processLabel);
    } catch {
      setError('Error al generar los comprobantes masivos');
    } finally {
      setDownloading(null);
    }
  };

  // Colores de encabezado de tabla por tipo
  const headerBg = tipo === 'nomina' ? 'bg-blue-800' : tipo === 'bonificacion' ? 'bg-purple-800' : 'bg-red-800';

  return (
    <div className="space-y-4">

      {/* Selector de tipo */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        {TIPOS.map(t => (
          <button
            key={t.id}
            onClick={() => setTipo(t.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              tipo === t.id
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Selector de proceso + botón bulk */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            {tipoConfig.label}
          </label>
          <div className="relative">
            <select
              value={processId}
              onChange={e => setProcessId(e.target.value)}
              disabled={loadingList}
              className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 min-w-[300px]"
            >
              <option value="">
                {loadingList ? 'Cargando...' : tipoConfig.emptyMsg}
              </option>
              {processes.map(p => (
                <option key={p.id} value={p.id}>
                  {buildProcessLabel(tipo, p)}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {records.length > 0 && (
          <button
            onClick={handleDownloadBulk}
            disabled={!!downloading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-60 transition-colors"
          >
            {downloading === 'bulk'
              ? <><Loader2 size={15} className="animate-spin" /> Generando ZIP...</>
              : <><Package size={15} /> Descargar todos ({records.length})</>
            }
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={15} className="flex-shrink-0" /> {error}
        </div>
      )}

      {/* Estado vacío */}
      {!processId && !loadingList && (
        <div className="flex flex-col items-center justify-center py-14 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FileText className="w-9 h-9 text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-500">{tipoConfig.emptyMsg}</p>
          <p className="text-xs text-gray-400 mt-1">{tipoConfig.emptyHint}</p>
        </div>
      )}

      {/* Cargando registros */}
      {loadingRecs && (
        <div className="flex items-center justify-center py-10">
          <RefreshCw size={18} className="text-blue-400 animate-spin mr-2" />
          <span className="text-sm text-gray-500">Cargando empleados...</span>
        </div>
      )}

      {/* Tabla de registros */}
      {!loadingRecs && records.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Users size={14} className="text-gray-400" />
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-gray-700">{records.length}</span> empleados
            </span>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={`${headerBg} text-white text-xs`}>
                  <th className="px-3 py-2.5 text-left font-semibold">Código</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Empleado</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Departamento</th>
                  <th className="px-3 py-2.5 text-right font-semibold">{tipoConfig.montoLabel}</th>
                  <th className="px-3 py-2.5 text-center font-semibold">Recibo</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec, idx) => (
                  <tr
                    key={rec.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    <td className="px-3 py-2 text-gray-500 text-xs">{rec.codigo}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {rec.nombre} {rec.apellido}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{rec.departamento || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800 font-medium">
                      {fmt(rec.monto)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleDownloadOne(rec)}
                        disabled={!!downloading}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 border border-gray-200 bg-gray-50 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
                      >
                        {downloading === rec.id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Download size={12} />
                        }
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-right">
              {records.length} empleados
            </div>
          </div>
        </>
      )}

      {/* Sin empleados */}
      {!loadingRecs && processId && records.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-400">
          No se encontraron registros para este proceso.
        </div>
      )}
    </div>
  );
}
